import type { MarketplaceId } from "@nova-ai/shared"
import { humanizeMarketplaceId } from "../../lib/marketplace"
import { hasRunsFilters, type RunsSearch } from "../../lib/runs-list"
import { Combobox } from "../ui/combobox"
import { splitActor } from "./actor-label"

export function RunsFilters({
  search,
  onChange,
  onClear,
  marketplaces,
  startedBy,
  count,
}: {
  search: RunsSearch
  onChange: <Key extends keyof RunsSearch>(key: Key, value: string | undefined) => void
  onClear: () => void
  marketplaces: readonly MarketplaceId[]
  startedBy: readonly string[]
  count: number | undefined
}) {
  return (
    <div
      data-runs-filters
      className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-border px-4 py-3 sm:gap-2"
    >
      <Combobox
        className="w-full flex-[1_1_100%] sm:max-w-[220px] sm:flex-none sm:w-[157.5px]"
        triggerClassName="h-10 min-h-8 cursor-pointer gap-2 pr-[9px] pl-3 text-[13px] font-normal shadow-none [&>span]:text-foreground [&[title]]:border-foreground motion-reduce:transition-none sm:h-8 focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2"
        aria-label="Marketplace"
        value={search.marketplace}
        options={marketplaces.map((marketplace) => ({
          value: marketplace,
          label: humanizeMarketplaceId(marketplace),
        }))}
        placeholder="All marketplaces"
        clearLabel="All marketplaces"
        searchPlaceholder="Search marketplaces…"
        onChange={(value) => onChange("marketplace", value)}
      />

      <Combobox
        className="w-full flex-[1_1_100%] sm:max-w-[220px] sm:flex-none sm:w-[220px]"
        triggerClassName="h-10 min-h-8 cursor-pointer gap-2 pr-[9px] pl-3 text-[13px] font-normal shadow-none [&>span]:text-foreground [&[title]]:border-foreground motion-reduce:transition-none sm:h-8 focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2"
        aria-label="Started by"
        value={search.startedBy}
        options={startedBy.map((actor) => ({ value: actor, label: splitActor(actor).handle }))}
        placeholder="Anyone"
        clearLabel="Anyone"
        searchPlaceholder="Search people…"
        onChange={(value) => onChange("startedBy", value)}
      />

      <Combobox
        className="w-full flex-[1_1_100%] sm:max-w-[220px] sm:flex-none sm:w-[105.5px]"
        triggerClassName="h-10 min-h-8 cursor-pointer gap-2 pr-[9px] pl-3 text-[13px] font-normal shadow-none [&>span]:text-foreground [&[title]]:border-foreground motion-reduce:transition-none sm:h-8 focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2"
        aria-label="Origin"
        value={search.origin}
        options={[
          { value: "operator", label: "Operator" },
          { value: "automatic", label: "Auto relist" },
        ]}
        placeholder="All origins"
        clearLabel="All origins"
        searchPlaceholder="Search origins…"
        onChange={(value) => onChange("origin", value)}
      />

      <label className="inline-flex h-11 cursor-pointer items-center gap-2 px-1 text-[13px] sm:h-8">
        <input
          type="checkbox"
          className="m-[3px_3px_3px_4px] size-4 accent-foreground scheme-light dark:scheme-dark focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2"
          checked={search.archived === "true"}
          onChange={(event) => onChange("archived", event.target.checked ? "true" : undefined)}
        />
        Include archived
      </label>

      <span className="text-[12px] text-muted-foreground tabular-nums" aria-live="polite">
        {count === undefined ? "— runs" : `${count} ${count === 1 ? "run" : "runs"}`}
      </span>

      {hasRunsFilters(search) && (
        <button
          type="button"
          className="ml-auto min-h-11 cursor-pointer text-[13px] font-medium text-foreground underline underline-offset-3 sm:min-h-0 focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2"
          onClick={onClear}
        >
          Clear filters
        </button>
      )}
    </div>
  )
}
